/* eslint-disable import/no-unresolved */
/* eslint-disable camelcase */

import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { ControlledMenu, MenuButton, MenuItem, useClick } from '@szhsin/react-menu';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { getWithdrawalLimitData, walletBalanceTopUp } from 'actions/trade';
import { DESKTOP_WALLET_OPEN } from 'actionTypes/wallet';
import { AccountRefreshTrigger } from 'components/AccountRefreshTrigger/AccountRefreshTrigger';
import Render from 'components/render';
import TransferFundModal from 'components/sub_account/trasnferFundModal';
import { IS_DEMO, IS_INDIAN_EXCHANGE, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { MIXPANEL_SESSION_REPLAY_CLASS, trackMixpanelEvent } from 'helpers/mixpanel-init';
import { getLoggedUserType, getSubAccounts } from 'helpers/utils';
import subscribe from 'HOC/subscribe';
import useLocalStorage from 'hooks/useLocalStorage';
import useToggle from 'hooks/useToggle';
import i18n from 'i18n/config';
import { selectIsSubAccountHasSameEmail } from 'selectors/account';
import { userSelector } from 'selectors/otherSelectors';
import { selectIsAuthChannelFailed } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { ArrowRight, Eye, EyeSlash, PlusIcon, WalletIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton/DeltaButton';
import ToggleExpandCollapseIcon from 'UIKit/ToggleExpandCollapseIcon';

import { TotalEquity } from '../computeTotalBalance';
import styles from './indianWallet.module.scss';
import MenuBottom from './MenuBottom';
import TooltipWrapper from './TooltipWrapper';

const getCtaURL = (isSubAccountHasSameEmail: boolean) => {
  if (IS_INDIAN_EXCHANGE && isSubAccountHasSameEmail) {
    return null;
  }
  return routes.account.deposit;
};

const getWalletCallToActionText = (t: any, isSubAccountHasSameEmail: boolean) => {
  if (IS_DEMO) {
    return t('headerFooter:header.wallet.reloadWallet');
  }
  if (!IS_INDIAN_EXCHANGE) {
    return t('headerFooter:header.wallet.addMoney');
  }
  if (IS_INDIAN_EXCHANGE && isSubAccountHasSameEmail) {
    return t('account:subAccount.transferFundTitle');
  }
  return t('headerFooter:header.wallet.addFunds');
};

const accountValueLabel = i18n.t('headerFooter:header.wallet.accountValue');

const accountRefreshTriggerLabel = i18n.t(
  'headerFooter:accountDataIsUpdatingPeriodically'
);

export const IndianExchangeWalletComponent = subscribe(
  ({ onClose, handleAddFundEvent, isSubAccountHasSameEmail }) => {
    const { t } = useTranslation(['headerFooter', 'account']);
    const [showAmount, setShowAmount] = useLocalStorage(STORAGE_KEYS.SHOW_BALANCE, true);

    const ctaURl = getCtaURL(isSubAccountHasSameEmail);
    const walletCallToActionText = getWalletCallToActionText(t, isSubAccountHasSameEmail);

    const toggleShowAmount = (event: React.MouseEvent) => {
      event.stopPropagation();
      setShowAmount(prev => !prev);
    };

    const appDispatch = useAppDispatch();
    const isAuthChannelDisconnected = useAppSelector(selectIsAuthChannelFailed);

    useEffect(() => {
      appDispatch(getWithdrawalLimitData(VANILLA_SETTLING_ASSET));
    }, [appDispatch]);

    const handleWalletMenuClick = e => {
      e.stopPropagation();
    };

    return (
      (<div
        className={cx(styles['wallet-menu'], styles.walletMenuContainer)}
        onClick={handleWalletMenuClick}
        data-testid="wallet-menu-click"
        role="menu"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleWalletMenuClick(e);
          }
        }}
        tabIndex={0}
        data-palette="IndianExchangeWalletComponent">
        <Render when={isAuthChannelDisconnected}>
          <AccountRefreshTrigger
            className={styles.accountRefreshTriggerContainer}
            message={accountRefreshTriggerLabel}
          />
        </Render>
        <div className={styles.walletMenuTopContainer}>
          {/* ACCOUNT VALUE */}
          <div className={styles['account-value']}>
            <div className={styles.label}>
              <TooltipWrapper
                title={accountValueLabel}
                body={t('headerFooter:header.wallet.accountValuePopOverIndianExchange')}
              />
              <span className={styles.eye}>
                <ToggleExpandCollapseIcon
                  isExpanded={showAmount}
                  onClick={toggleShowAmount}
                  customExpandIcon={<Eye width={18} height={18} />}
                  customCollapseIcon={<EyeSlash width={18} height={18} />}
                  data-testid="toggle-show-amount"
                />
              </span>
              <div className={styles.pnlAnalyticsLink}>
                <Link to={routes.account.accountAnalytics.default}>
                  {t('headerFooter:header.wallet.pnlAnalytics')}
                  <ArrowRight className={styles.pnlAnalyticsArrowRight} />
                </Link>
              </div>
            </div>
            <div className={cx(styles.value, MIXPANEL_SESSION_REPLAY_CLASS)}>
              <span className={styles.inr}>
                <Render when={showAmount} else="******">
                  <TotalEquity
                    isInr
                    isForWalletIndianExchange
                    shouldPrettifyINR
                    withTrackerEquity
                  />
                </Render>
              </span>
              <span className={styles.usd}>
                <Render when={showAmount} else="******">
                  <TotalEquity isForWalletIndianExchange withTrackerEquity />
                </Render>
              </span>
            </div>
          </div>

          <Box flexPosition="flex-row-spread" gap={16}>
            <DeltaButton
              data-testid="add-money-btn"
              variant="primary-t2"
              as={IS_DEMO ? 'button' : Link}
              to={ctaURl}
              className={styles.walletCtas}
              onClick={handleAddFundEvent}
              icon={<PlusIcon width={10} height={10} color="var(--white)" />}
              wrapperClassName={styles.walletCallToActionTextContainer}
            >
              {walletCallToActionText}
            </DeltaButton>
            {!IS_DEMO && (
              <DeltaButton
                data-testid="withdraw-btn"
                variant="secondary-t1"
                LinkComponent={Link}
                to={routes.account.withdrawal}
                className={styles.walletCtas}
                onClick={onClose}
              >
                {t('headerFooter:header.wallet.withdraw')}
              </DeltaButton>
            )}
          </Box>
        </div>
        <MenuBottom showAmount={showAmount} />
      </div>)
    );
  }
);

const USDBalance = React.memo(({ children }: { children: ReactNode }) => {
  return (
    <Box className={cx(styles.balance, MIXPANEL_SESSION_REPLAY_CLASS)}>
      <WalletIcon width={18} height={18} />
      {children}
    </Box>
  );
});

const INRBalance = React.memo(({ children }: { children: ReactNode }) => {
  return (
    <Box className={cx(styles.balance, MIXPANEL_SESSION_REPLAY_CLASS)}>
      <WalletIcon width={18} height={18} />
      {children}
    </Box>
  );
});

const WalletMenu = ({ showMaskedAmount = false }: { showMaskedAmount?: boolean }) => {
  const [isHoveredOnBalance, setIsHoveredOnBalance] = useState<boolean>(false);
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const { onClick, ...restAnchorProps } = useClick(isOpen, setOpen);
  const isAuthChannelDisconnected = useAppSelector(selectIsAuthChannelFailed);
  const location = useLocation();
  const isTradePage = !!matchPath(
    location.pathname,
    '/:contractType?/trade/:assetSymbol?/:productSymbol?'
  )?.isExact;
  const isPositionsPage = !!matchPath(location.pathname, '/account/positions')?.isExact;
  const isTradeorPositionsPage = isTradePage || isPositionsPage;
  const appDispatch = useAppDispatch();
  const [showAmount] = useLocalStorage(STORAGE_KEYS.SHOW_BALANCE, true);
  const onMenuClick = event => {
    onClick(event);
    trackMixpanelEvent('Wallet Clicked - All Pages - Header');
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isAuthChannelDisconnected && isOpen && !isTradeorPositionsPage) {
      appDispatch({ type: DESKTOP_WALLET_OPEN, payload: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDispatch, isOpen]);

  const [openTransferFundModal, toggleTransferFundModal] = useToggle(false);
  const user = useAppSelector(userSelector);
  const isSubAccountHasSameEmail = useAppSelector(selectIsSubAccountHasSameEmail);
  const loggedUserType = getLoggedUserType(user);

  const subAccounts = getSubAccounts(user, loggedUserType);

  const handleAddFundEvent = () => {
    if (IS_DEMO) {
      appDispatch(walletBalanceTopUp());
    }
    if (IS_INDIAN_EXCHANGE && isSubAccountHasSameEmail) {
      toggleTransferFundModal();
    } else {
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.DEPOSITS.ADD_FUND_CLICKED);
    }
    onClose();
  };

  return (
    <>
      <TransferFundModal
        isModalOpen={openTransferFundModal}
        handleCloseModal={toggleTransferFundModal}
        subAccounts={subAccounts}
        user={user}
      />
      <MenuButton
        ref={ref}
        data-testid="indianExchangeWallet"
        className={styles.button}
        id="wallet-button"
        onClick={onMenuClick}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...restAnchorProps}
      >
        <Box
          className={cx(styles.balanceContainer, MIXPANEL_SESSION_REPLAY_CLASS, {
            [styles.walletMenuOpened]: isOpen,
          })}
          onMouseEnter={() => setIsHoveredOnBalance(true)}
          onMouseLeave={() => setIsHoveredOnBalance(false)}
        >
          <Render
            when={isHoveredOnBalance}
            else={
              <INRBalance>
                <Render when={showAmount && !showMaskedAmount} else="*****">
                  <TotalEquity
                    isInr
                    isForWalletIndianExchange
                    shouldPrettifyINR
                    withTrackerEquity
                  />
                </Render>
              </INRBalance>
            }
          >
            <USDBalance>
              <Render when={showAmount && !showMaskedAmount} else="*****">
                <TotalEquity
                  isForWalletIndianExchange
                  shouldPrettifyUSD
                  withTrackerEquity
                />
              </Render>
            </USDBalance>
          </Render>
        </Box>
      </MenuButton>
      <ControlledMenu
        state={isOpen ? 'open' : 'closed'}
        onClose={() => {
          setOpen(false);
          if (isAuthChannelDisconnected && !isTradeorPositionsPage)
            appDispatch({ type: DESKTOP_WALLET_OPEN, payload: false });
        }}
        anchorRef={ref}
        className={styles['wallet-menu-container']}
      >
        <MenuItem>
          <Render when={isOpen}>
            <IndianExchangeWalletComponent
              onClose={onClose}
              handleAddFundEvent={handleAddFundEvent}
              isSubAccountHasSameEmail={isSubAccountHasSameEmail}
            />
          </Render>
        </MenuItem>
      </ControlledMenu>
    </>
  );
};

export default WalletMenu;
